import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {QlQueryContentFieldDto} from 'features/core/shared/ql/data/dto/ql-query-content-field.dto';
import {ProvisionTransactionEvent} from '../../../../../enum/provision-transaction-event.enum';
import {ProvisionTransactionStatus} from '../../../../../enum/provision-transaction-status.enum';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';

/**
 * Field shape of the QL `dateGroup` field type.
 *
 * The backend knows three field types (`QlQueryContentFieldType`: regular | function | dateGroup)
 * and translates `dateGroup` into `DATE_TRUNC('<precision>', column)` — allowed precisions are
 * `day`, `month`, `year` (`QlJdbcSqlBuilder.ALLOWED_DATE_TRUNC_PRECISIONS`). The shared
 * frontend DTO `QlQueryContentFieldDto` models only `regular` and `function` so far; this local
 * extension adds the two missing attributes WITHOUT touching a core file.
 *
 * VERIFIED, DELIBERATE WORKAROUND (review finding 19): the interface and the double cast in
 * {@link CpProvisionAgentPayoutsComponent.buildQlQuery} stay as they are. They are not
 * type-cheating but the only bridge over a real gap: the backend CAN handle
 * `dateGroup`, the shared core DTO does not yet KNOW the field. Reworking the core DTO is
 * outside this module's scope (it belongs to all modules); dropping `dateGroup` would mean
 * rebuilding the required monthly aggregation (04-frontend.md §7) in the frontend.
 *
 * CAN BE REMOVED once `QlQueryContentFieldDto` itself carries `fieldType: 'dateGroup'` and
 * `dateGroupPrecision`: then delete this interface, type the field literal directly as
 * `QlQueryContentFieldDto`, and remove the cast — nothing else.
 */
interface CpQlDateGroupField extends Omit<QlQueryContentFieldDto, 'fieldType'> {
    fieldType: 'dateGroup';
    dateGroupPrecision: 'day' | 'month' | 'year';
}

/**
 * navItem "Auszahlungen pro Monat" of the ProvisionAgent OBC (04-frontend.md §7), widget alias
 * `cp.agent.payouts.month.ql`.
 *
 * QL AGGREGATION over `cp.ProvisionAgentTransaction`:
 *   SUM(amount) + COUNT(*)
 *   Filter: `event = PAYOUT` AND `status = ACTIVE` AND `provisionAgent = <agent>`
 *   Grouping: month of `valutaDate` (DATE_TRUNC month)
 *
 * The filter on ACTIVE is a business necessity: a payout that is only CALCULATED has not yet
 * been paid out and must not appear in a monthly total.
 *
 * QL shape exactly following the documented repo example `bm-creditor-transaction-analysis.component.ts`
 * (`start.type = 'groupBy'` with mixed `regular`/`function` fields). Extended with the
 * `dateGroup` field for the monthly grouping.
 *
 * WORKAROUND NOTE (review finding 19, verified): the month field is built as
 * {@link CpQlDateGroupField} and passed into the core DTO via a double cast. This is
 * DELIBERATE and stays this way until the core DTO knows `dateGroup` — rationale and
 * removal recipe are in the Javadoc of {@link CpQlDateGroupField}.
 *
 * REFRESH (04-frontend.md §4, review finding 21b): the monthly totals are run results —
 * the payout run writes the PAYOUT bookings they are built from. The component therefore
 * subscribes, like the sibling panes, to `JbSpringBatchIndicatorService.status$` and reloads on
 * the running -> not-running transition; it additionally reacts to `objectChangeEvent` for
 * `cp.ProvisionAgentTransaction`, so a manual booking is reflected in the totals immediately.
 *
 * ROBUSTNESS NOTE: `dateGroup` resolves only over real date COLUMNS. `valutaDate` is
 * a LocalDate column of the entity and therefore suitable — unlike audit/instant fields,
 * for which the approach is documented in the repo as NOT working (see hb-config.page.ts, WI-09).
 */
@Component({
    selector: 'cp-provision-agent-payouts',
    templateUrl: './cp-provision-agent-payouts.component.html',
    styleUrls: ['./cp-provision-agent-payouts.component.scss'],
    standalone: false
})
export class CpProvisionAgentPayoutsComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionAgentId: number;

    /** Tabular monthly totals. */
    payoutsTableWidget: WidgetData;

    /** The same aggregation as a bar chart — the trend is faster to read than the table. */
    payoutsChartWidget: WidgetData;

    /** Is a cp job currently running? The monthly totals are then an interim state. */
    jobRunning: boolean;

    protected statusSubscription: Subscription;
    protected objectChangeSubscription: Subscription;
    protected jobWasRunning: boolean;

    constructor(
        protected mvsObjectService: MvsObjectService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.subscribeToObjectChanges();
        this.subscribeToJobCompletion();
        this.refreshComponent();
    }

    /** A manual PAYOUT booking changes the monthly total immediately. */
    protected subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'cp.ProvisionAgentTransaction') {
                this.refreshComponent();
            }
        });
    }

    /**
     * Subscription to the job indicator; reload on the running -> not-running transition (04 §4).
     *
     * The indicator is neither built nor activated here — that is done by the shell CpDashboardPage
     * (`jobNamePattern: 'cp%'`). If the OBC runs outside that shell, `status$` simply never
     * delivers a transition; the component then behaves as before.
     *
     * BOTH widgets (table and chart) are rebuilt here — unlike the panes with a single table,
     * there is no shared instance here that could be reloaded in place, and both depend on the
     * same aggregation.
     */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.refreshComponent();
            }
        });
    }

    refreshComponent(): void {

        if (!this.provisionAgentId) {
            this.payoutsTableWidget = null;
            this.payoutsChartWidget = null;
            this.initialized = true;
            return;
        }

        const qlQuery = this.buildQlQuery();

        /**
         * AI: Monthly total of an agent's payouts.
         *     Source: QL groupBy on cp.ProvisionAgentTransaction, event = PAYOUT, status = ACTIVE,
         *     grouped by month of the valuta date.
         *     Caseworkers see here what was actually paid out in which month.
         */
        this.payoutsTableWidget = WidgetFactory.createWidgetTableQl(
            'cp.agent.payouts.month.ql',
            'Auszahlungen pro Monat',
            qlQuery,
            WidgetDataParam.create('paging', 24),
            WidgetDataParam.create('size', 'S')
        );

        /**
         * AI: Bar chart of the same monthly aggregation.
         *     Source: identical QL query, rendered as a chart.
         *     Shows the payout trend at a glance.
         */
        this.payoutsChartWidget = WidgetFactory.createWidgetListQl(
            'cp.agent.payouts.month.ql.chart',
            'Auszahlungsverlauf',
            'chart',
            this.buildQlQuery(),
            WidgetDataParam.create('chartType', 'bar'),
            WidgetDataParam.create('chartField1st', 'e.payoutMonth'),
            WidgetDataParam.create('chartFieldAggregate', 'e.payoutAmount'),
            WidgetDataParam.create('chartAggregateFunction', 'sum'),
            WidgetDataParam.create('chartStacked', false),
            WidgetDataParam.create('size', 'M')
        );

        this.initialized = true;
    }

    /**
     * Builds the QL aggregation. Deliberately its own method: table and chart each need their
     * OWN instance of the query object — a shared literal would be mutated by two widgets.
     */
    private buildQlQuery(): QlQueryDto {

        const payoutMonthField: CpQlDateGroupField = {
            name: 'valutaDate',
            as: 'payoutMonth',
            label: 'Monat',
            dataType: 'LocalDate',
            fieldType: 'dateGroup',
            dateGroupPrecision: 'month'
        };

        const fields: QlQueryContentFieldDto[] = [
            // Double cast: deliberate, documented workaround — see the Javadoc of CpQlDateGroupField.
            // `fieldType` is narrowed to 'regular' | 'function' in the core DTO, so the
            // direct cast is structurally invalid and the detour via `unknown` is necessary. The
            // payload itself is correct: the backend reads exactly these two attributes.
            // Can be removed once the core DTO knows 'dateGroup'.
            payoutMonthField as unknown as QlQueryContentFieldDto,
            {
                name: 'amount',
                as: 'payoutAmount',
                function: 'sum',
                label: 'Summe',
                dataType: 'BigDecimal',
                fieldType: 'function'
            },
            {
                name: '*',
                as: 'payoutCount',
                function: 'count',
                label: 'Anzahl',
                dataType: 'Long',
                fieldType: 'function'
            }
        ];

        return {
            name: 'cp.agent.payouts.month',
            type: 'view',
            start: {
                type: 'groupBy',
                name: 'cp.ProvisionAgentTransaction',
                as: 'e',
                fields: fields
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionAgentId', FilterCriteria.cOperatorEqual, this.provisionAgentId),
                FilterCriteria.create('e.event', FilterCriteria.cOperatorEqual, ProvisionTransactionEvent.PAYOUT),
                FilterCriteria.create('e.status', FilterCriteria.cOperatorEqual, ProvisionTransactionStatus.ACTIVE)
            ],
            sortings: [new Sorting('e.payoutMonth', false)]
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionAgentId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.objectChangeSubscription?.unsubscribe();
        this.statusSubscription?.unsubscribe();
    }

}
